import { uiText } from '../../utils/uiTranslation';
import { ANALYTICS_SCREEN_IDS } from './analyticsScreenDefaults';
import { getAnalyticsMetricLabel } from '../../services/atsAnalyticsService';

export const EXPORT_SECTION_TYPES = {
  KPI_STRIP: 'kpi_strip',
  RANKED_TABLE: 'ranked_table',
  BAR_TABLE: 'bar_table',
  TREND: 'trend',
  TARGET_PROGRESS: 'target_progress',
  ACTION_LIST: 'action_list',
  NARRATIVE: 'narrative',
  CONFIDENCE_NOTES: 'confidence_notes'
};

const DEFAULT_JOB_STATUSES = ['published', 'closed', 'unpublished', 'pending_approval', 'draft', 'deleted'];
const EMPTY_ARRAY = [];

export const REPORT_CENTER_TEMPLATES = [
  {
    id: 'monthly_hiring_health',
    label: 'Monthly Hiring Health',
    subtitle: 'Leadership-ready view of hiring health across funnel, role risk, sources, quality, team execution, and actions.',
    sectionIds: [
      'executive_summary',
      'funnel_conversion',
      'open_role_risk',
      'historical_role_risk',
      'source_performance',
      'quality_signals',
      'team_execution',
      'recommended_actions',
      'data_confidence'
    ]
  },
  {
    id: 'pipeline_risk_review',
    label: 'Pipeline Risk Review',
    subtitle: 'Operational review of aging roles, bottlenecks, stalled offers, forecast risk, and recommended actions.',
    sectionIds: [
      'open_role_risk',
      'aging_roles',
      'historical_role_risk',
      'stage_bottlenecks',
      'offer_health',
      'vacancy_forecast',
      'recommended_actions',
      'data_confidence'
    ]
  },
  {
    id: 'source_roi_quality',
    label: 'Source ROI & Quality',
    subtitle: 'Source volume, hires, hire rate, efficiency, and downstream quality signals.',
    sectionIds: [
      'source_performance',
      'source_quality',
      'funnel_conversion',
      'quality_signals',
      'recommended_actions',
      'data_confidence'
    ]
  },
  {
    id: 'recruiting_ops_weekly',
    label: 'Recruiting Ops Weekly',
    subtitle: 'Weekly operating packet for pipeline movement, recruiter load, stalled candidates, offer status, and urgent actions.',
    sectionIds: [
      'executive_summary',
      'pipeline_backlog',
      'team_execution',
      'offer_health',
      'open_role_risk',
      'recommended_actions'
    ]
  }
];

const CANONICAL_SCREEN_MANIFESTS = {
  [ANALYTICS_SCREEN_IDS.EXECUTIVE_OVERVIEW]: {
    title: 'Executive Overview Export',
    subtitle: 'KPI summary, headcount targets, attention items, funnel intelligence, and action narrative.',
    sectionIds: ['executive_summary', 'headcount_targets', 'recommended_actions', 'funnel_conversion', 'data_confidence']
  },
  [ANALYTICS_SCREEN_IDS.RECRUITMENT]: {
    title: 'Recruitment Export',
    subtitle: 'Funnel conversion, source performance, traffic/source mix, offer funnel, and hiring target progress.',
    sectionIds: ['funnel_conversion', 'source_performance', 'pipeline_backlog', 'offer_health', 'headcount_targets', 'data_confidence']
  },
  [ANALYTICS_SCREEN_IDS.PIPELINE_RISK]: {
    title: 'Pipeline & Risk Export',
    subtitle: 'Open role risk, stage bottlenecks, offer stalling, vacancy risk, and candidate drop-off actions.',
    sectionIds: ['open_role_risk', 'historical_role_risk', 'stage_bottlenecks', 'offer_health', 'vacancy_forecast', 'recommended_actions', 'data_confidence']
  },
  [ANALYTICS_SCREEN_IDS.QUALITY_OF_HIRES]: {
    title: 'Quality of Hires Export',
    subtitle: 'Onboarding health, early quality, retention, source quality, and probation signals.',
    sectionIds: ['quality_signals', 'source_quality', 'onboarding_health', 'retention_signals', 'probation_signals', 'data_confidence']
  },
  [ANALYTICS_SCREEN_IDS.RECRUITER_PERFORMANCE]: {
    title: 'Recruiter Performance Export',
    subtitle: 'Recruiter scorecard, workload, velocity, conversion, offer outcomes, and retention performance.',
    sectionIds: ['team_execution', 'recruiter_workload', 'recruiter_velocity', 'pipeline_conversion', 'offer_health', 'data_confidence']
  }
};

export const EXPORT_WIDGET_SOURCES = {
  headcount_targets: ANALYTICS_SCREEN_IDS.EXECUTIVE_OVERVIEW,
  funnel_intelligence: ANALYTICS_SCREEN_IDS.EXECUTIVE_OVERVIEW,
  requires_attention: ANALYTICS_SCREEN_IDS.EXECUTIVE_OVERVIEW,
  stage_conversion_waterfall: ANALYTICS_SCREEN_IDS.RECRUITMENT,
  source_quality_matrix: ANALYTICS_SCREEN_IDS.RECRUITMENT,
  applicant_traffic_map: ANALYTICS_SCREEN_IDS.RECRUITMENT,
  offer_waterfall: ANALYTICS_SCREEN_IDS.RECRUITMENT,
  headcount_targets_by_tag_group: ANALYTICS_SCREEN_IDS.RECRUITMENT,
  open_roles_risk: ANALYTICS_SCREEN_IDS.PIPELINE_RISK,
  stage_bottleneck: ANALYTICS_SCREEN_IDS.PIPELINE_RISK,
  offer_stalling: ANALYTICS_SCREEN_IDS.PIPELINE_RISK,
  vacancy_forecast: ANALYTICS_SCREEN_IDS.PIPELINE_RISK,
  candidate_dropoff_risk: ANALYTICS_SCREEN_IDS.PIPELINE_RISK,
  onboarding_health: ANALYTICS_SCREEN_IDS.QUALITY_OF_HIRES,
  early_quality_index: ANALYTICS_SCREEN_IDS.QUALITY_OF_HIRES,
  retention_timeline: ANALYTICS_SCREEN_IDS.QUALITY_OF_HIRES,
  source_quality: ANALYTICS_SCREEN_IDS.QUALITY_OF_HIRES,
  probation_performance: ANALYTICS_SCREEN_IDS.QUALITY_OF_HIRES,
  recruiter_scorecard: ANALYTICS_SCREEN_IDS.RECRUITER_PERFORMANCE,
  recruiter_radar: ANALYTICS_SCREEN_IDS.RECRUITER_PERFORMANCE,
  load_vs_outcome: ANALYTICS_SCREEN_IDS.RECRUITER_PERFORMANCE,
  time_to_fill: ANALYTICS_SCREEN_IDS.RECRUITER_PERFORMANCE,
  pipeline_conversion: ANALYTICS_SCREEN_IDS.RECRUITER_PERFORMANCE,
  recruiter_velocity: ANALYTICS_SCREEN_IDS.RECRUITER_PERFORMANCE,
  offer_performance: ANALYTICS_SCREEN_IDS.RECRUITER_PERFORMANCE,
  retention_by_recruiter: ANALYTICS_SCREEN_IDS.RECRUITER_PERFORMANCE
};

const SECTION_WIDGET_IDS = {
  headcount_targets: ['headcount_targets_by_tag_group', 'headcount_targets'],
  open_role_risk: ['open_roles_risk'],
  aging_roles: ['open_roles_risk'],
  stage_bottlenecks: ['stage_bottleneck'],
  offer_health: ['offer_waterfall', 'offer_stalling', 'offer_performance'],
  vacancy_forecast: ['vacancy_forecast'],
  source_quality: ['source_quality', 'source_quality_matrix'],
  quality_signals: ['early_quality_index', 'onboarding_health', 'probation_performance'],
  onboarding_health: ['onboarding_health'],
  retention_signals: ['retention_timeline', 'retention_by_recruiter'],
  probation_signals: ['probation_performance'],
  team_execution: ['recruiter_scorecard', 'load_vs_outcome', 'recruiter_radar'],
  recruiter_workload: ['load_vs_outcome', 'recruiter_scorecard'],
  recruiter_velocity: ['recruiter_velocity', 'time_to_fill'],
  pipeline_conversion: ['pipeline_conversion'],
  pipeline_backlog: ['stage_conversion_waterfall'],
  recommended_actions: ['candidate_dropoff_risk', 'requires_attention']
};

const SECTION_TITLES = {
  executive_summary: 'Executive Summary',
  funnel_conversion: 'Hiring Funnel',
  open_role_risk: 'Open Role Risk',
  historical_role_risk: 'Historical Role Risk',
  source_performance: 'Source Performance',
  quality_signals: 'Quality Signals',
  team_execution: 'Team Execution',
  recommended_actions: 'Actions & Risks',
  data_confidence: 'Data Confidence',
  aging_roles: 'Aging Roles',
  stage_bottlenecks: 'Stage Bottlenecks',
  offer_health: 'Offer Health',
  vacancy_forecast: 'Vacancy Forecast',
  source_quality: 'Source Quality',
  pipeline_backlog: 'Pipeline Backlog',
  headcount_targets: 'Headcount Targets',
  onboarding_health: 'Onboarding Health',
  retention_signals: 'Retention Signals',
  probation_signals: 'Probation Signals',
  recruiter_workload: 'Recruiter Workload',
  recruiter_velocity: 'Recruiter Velocity',
  pipeline_conversion: 'Pipeline Conversion'
};

const SECTION_DESCRIPTIONS = {
  executive_summary: 'Headline hiring metrics and leadership context.',
  funnel_conversion: 'Stage-by-stage conversion and drop-off.',
  open_role_risk: 'Roles with elevated aging, pipeline depth, or stalled candidate pressure.',
  historical_role_risk: 'Roles that became high-risk, stayed high-risk, or created vacancy drag during the selected window.',
  source_performance: 'Applicant source volume, hires, hire rate, and efficiency.',
  quality_signals: 'Quality and downstream hiring health indicators.',
  team_execution: 'Recruiter/team execution, workload, and outcomes.',
  recommended_actions: 'Alerts, insights, and the next operational actions to review.',
  data_confidence: 'Coverage notes and excluded legacy counts for stakeholder interpretation.',
  aging_roles: 'Open roles that are aging without enough movement.',
  stage_bottlenecks: 'Stages where candidates are slowing down or dropping off.',
  offer_health: 'Offer progression and approval/conversion health.',
  vacancy_forecast: 'Forecasted vacancy pressure and fill-date risk.',
  source_quality: 'Quality outcomes by source where data is available.',
  pipeline_backlog: 'Current active candidate load by stage or segment.',
  headcount_targets: 'Hiring goal progress by department or target segment.',
  onboarding_health: 'Onboarding completion and post-hire handoff signals.',
  retention_signals: 'Early retention or cohort risk signals.',
  probation_signals: 'Probation and early performance indicators.',
  recruiter_workload: 'Load and outcome balance across recruiters.',
  recruiter_velocity: 'Recruiter speed and time-to-fill movement.',
  pipeline_conversion: 'Recruiter or team conversion through hiring stages.'
};

const clampRows = (rows = [], limit = 8) => (Array.isArray(rows) ? rows : []).filter(Boolean).slice(0, limit);

const safeNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const safePct = (value) => `${safeNumber(value).toFixed(Number.isInteger(Number(value)) ? 0 : 1)}%`;

export const getAnalyticsPeriodDays = (period = 'last_30_days') => {
  if (typeof period === 'number') return period;
  const key = String(period || '').toLowerCase();
  const fixed = {
    last_30_days: 30,
    last_60_days: 60,
    last_90_days: 90,
    last_180_days: 180,
    last_12_months: 365,
    last_24_months: 730
  };
  if (fixed[key]) return fixed[key];
  if (key === 'quarter_to_date') {
    const now = new Date();
    const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
    return Math.max(1, Math.ceil((now - quarterStart) / 86400000) + 1);
  }
  if (key === 'year_to_date') {
    const now = new Date();
    const yearStart = new Date(now.getFullYear(), 0, 1);
    return Math.max(1, Math.ceil((now - yearStart) / 86400000) + 1);
  }
  return 30;
};

const PERIOD_KEY_BY_DAYS = {
  30: 'last_30_days',
  60: 'last_60_days',
  90: 'last_90_days',
  180: 'last_180_days',
  365: 'last_12_months',
  730: 'last_24_months'
};

export const getAnalyticsPeriodKey = (period = 'last_30_days') => {
  if (typeof period === 'number') return PERIOD_KEY_BY_DAYS[period] || '';
  const value = String(period || '').trim();
  if (/^\d+$/.test(value)) return PERIOD_KEY_BY_DAYS[Number(value)] || '';
  return value;
};

export const buildAnalyticsWidgetQueryFilters = (filterState = {}, screenId = '') => {
  if (screenId === ANALYTICS_SCREEN_IDS.PIPELINE_RISK) return {};
  const filters = {};
  const period = getAnalyticsPeriodKey(filterState.period || filterState.dateRangeDays || '');
  if (period) filters.period = period;
  ['departments', 'roles', 'recruiters', 'sources', 'tags', 'locations'].forEach((key) => {
    const value = filterState[key];
    if (Array.isArray(value)) {
      const clean = value.filter((item) => item && item !== 'all');
      if (clean.length) filters[key] = clean;
      return;
    }
    if (value && value !== 'all') filters[key] = value;
  });
  return filters;
};

export const buildAnalyticsReportFilters = (filterState = {}) => {
  const selectedRoles = Array.isArray(filterState.roles)
    ? filterState.roles
    : filterState.roles && filterState.roles !== 'all'
      ? [filterState.roles]
      : [];

  return {
    dateRangeDays: getAnalyticsPeriodDays(filterState.period || filterState.dateRangeDays || 30),
    includeCompare: false,
    jobIds: [],
    jobTitles: selectedRoles.filter((role) => role && role !== 'all'),
    jobStatuses: DEFAULT_JOB_STATUSES,
    includeArchivedCandidates: true,
    includeRemovedCandidates: true
  };
};

export const getReportCenterTemplate = (templateId = '') =>
  REPORT_CENTER_TEMPLATES.find((template) => template.id === templateId) || REPORT_CENTER_TEMPLATES[0];

export const getReportCenterTemplateSections = (templateId = '') =>
  getReportCenterTemplate(templateId).sectionIds.map((id) => ({
    id,
    title: SECTION_TITLES[id] || id,
    description: SECTION_DESCRIPTIONS[id] || ''
  }));

const getWidgetRequestsForSections = (sectionIds = []) => {
  const seen = new Set();
  return (sectionIds || []).flatMap((sectionId) => SECTION_WIDGET_IDS[sectionId] || [])
    .reduce((requests, widgetId) => {
      const screenId = EXPORT_WIDGET_SOURCES[widgetId];
      const key = `${screenId}:${widgetId}`;
      if (!screenId || seen.has(key)) return requests;
      seen.add(key);
      requests.push({ screenId, widgetId });
      return requests;
    }, []);
};

const getOfferHealthWidgetIds = (screenId = '') => {
  if (screenId === ANALYTICS_SCREEN_IDS.RECRUITMENT) return ['offer_waterfall', 'offer_stalling', 'offer_performance'];
  if (screenId === ANALYTICS_SCREEN_IDS.PIPELINE_RISK) return ['offer_stalling', 'offer_waterfall', 'offer_performance'];
  if (screenId === ANALYTICS_SCREEN_IDS.RECRUITER_PERFORMANCE) return ['offer_performance', 'offer_waterfall', 'offer_stalling'];
  return ['offer_stalling', 'offer_waterfall', 'offer_performance'];
};

export const getCanonicalExportWidgetRequests = (screenId) => {
  const manifest = CANONICAL_SCREEN_MANIFESTS[screenId] || CANONICAL_SCREEN_MANIFESTS[ANALYTICS_SCREEN_IDS.EXECUTIVE_OVERVIEW];
  return getWidgetRequestsForSections(manifest.sectionIds);
};

export const getReportCenterWidgetRequests = (templateId = '', enabledSectionIds = null) => {
  const template = getReportCenterTemplate(templateId);
  const enabled = Array.isArray(enabledSectionIds) ? new Set(enabledSectionIds) : null;
  const sectionIds = enabled ? template.sectionIds.filter((sectionId) => enabled.has(sectionId)) : template.sectionIds;
  return getWidgetRequestsForSections(sectionIds);
};

const metricValue = (metricKey, value) => {
  if (value === null || value === undefined || value === '') return '0';
  if (String(metricKey).includes('Pct') || String(metricKey).includes('Rate')) return safePct(value);
  if (String(metricKey).includes('Days') || String(metricKey).includes('daysOpen')) return `${safeNumber(value)}d`;
  if (metricKey === 'pipelineVelocityPerDay') return uiText("{{count}}/day", { count: safeNumber(value) });
  return String(value);
};

const getMetric = (report, key) => report?.metrics?.[key] ?? report?.funnel?.[key] ?? 0;

const buildKpiSection = (report, { id = 'executive_summary', title = SECTION_TITLES.executive_summary } = {}) => ({
  id,
  title,
  description: SECTION_DESCRIPTIONS[id],
  type: EXPORT_SECTION_TYPES.KPI_STRIP,
  metrics: [
    'totalApplicants',
    'qualifiedRatePct',
    'hireRatePct',
    'timeToHireDays',
    'activePipelineCandidates',
    'stalledCandidates'
  ].map((metricKey) => ({
    id: metricKey,
    label: getAnalyticsMetricLabel(metricKey),
    value: metricValue(metricKey, getMetric(report, metricKey))
  }))
});

const buildFunnelSection = (report) => ({
  id: 'funnel_conversion',
  title: SECTION_TITLES.funnel_conversion,
  description: SECTION_DESCRIPTIONS.funnel_conversion,
  type: EXPORT_SECTION_TYPES.BAR_TABLE,
  columns: [
    { id: 'from', label: 'From' },
    { id: 'to', label: 'To' },
    { id: 'fromCount', label: 'Started' },
    { id: 'toCount', label: 'Converted' },
    { id: 'dropoffCount', label: 'Drop-off' },
    { id: 'conversionRatePct', label: 'Conversion' }
  ],
  rows: clampRows(report?.series?.stageConversions, 10).map((row, index) => ({
    id: row?.id || `stage-${index}`,
    label: row?.fromLabel && row?.toLabel ? `${row.fromLabel} -> ${row.toLabel}` : row?.label || `Stage ${index + 1}`,
    value: safeNumber(row?.dropoffCount),
    values: {
      from: row?.fromLabel || '',
      to: row?.toLabel || '',
      fromCount: safeNumber(row?.fromCount),
      toCount: safeNumber(row?.toCount),
      dropoffCount: safeNumber(row?.dropoffCount),
      conversionRatePct: safePct(row?.conversionRatePct)
    }
  }))
});

const buildSourceSection = (report, id = 'source_performance') => ({
  id,
  title: SECTION_TITLES[id] || SECTION_TITLES.source_performance,
  description: SECTION_DESCRIPTIONS[id] || SECTION_DESCRIPTIONS.source_performance,
  type: EXPORT_SECTION_TYPES.RANKED_TABLE,
  columns: [
    { id: 'source', label: 'Source' },
    { id: 'applications', label: 'Applicants' },
    { id: 'hires', label: 'Hires' },
    { id: 'hireRatePct', label: 'Hire rate' },
    { id: 'efficiencyIndex', label: 'Efficiency' }
  ],
  rows: clampRows(report?.series?.sourceEfficiency, 10).map((row, index) => ({
    id: row?.source || `source-${index}`,
    label: row?.source || 'Unknown source',
    value: safeNumber(row?.efficiencyIndex),
    values: {
      source: row?.source || 'Unknown source',
      applications: safeNumber(row?.applications),
      hires: safeNumber(row?.hires),
      hireRatePct: safePct(row?.hireRatePct),
      efficiencyIndex: safeNumber(row?.efficiencyIndex)
    }
  }))
});

const toCamelCase = (value = '') => String(value).replace(/_([a-z])/g, (_, char) => char.toUpperCase());

const DATASET_ARRAY_KEYS = [
  'rows',
  'categories',
  'items',
  'stages',
  'periods',
  'series',
  'countries',
  'profiles',
  'lanes',
  'points',
  'segments'
];

const datasetHasContent = (dataset = null) =>
  Boolean(dataset && DATASET_ARRAY_KEYS.some((key) => Array.isArray(dataset[key]) && dataset[key].length));

const getFirstDataset = (datasets = {}) => {
  const values = Object.values(datasets || {}).filter(Boolean);
  return values.find(datasetHasContent) || values[0] || null;
};

const getWidgetDataset = (widgetModels = [], widgetId = '') => {
  const model = (widgetModels || []).find((item) => item?.id === widgetId);
  if (model?.dataset) return model.dataset;
  if (model?.datasets && model?.activeGraph && model.datasets[model.activeGraph]) return model.datasets[model.activeGraph];
  if (model?.datasets) return getFirstDataset(model.datasets);
  return null;
};

const getDashboardWidget = (dashboardWidgets = {}, widgetId = '') =>
  dashboardWidgets[widgetId] || dashboardWidgets[toCamelCase(widgetId)] || null;

const getDashboardWidgetDataset = (dashboardWidgets = {}, widgetId = '') => {
  const widget = getDashboardWidget(dashboardWidgets, widgetId);
  if (widget?.dataset) return widget.dataset;
  if (widget?.payload?.dataset) return widget.payload.dataset;
  return getFirstDataset(widget?.payload?.datasets || widget?.datasets || {});
};

const getDashboardCategories = (dashboardWidgets = {}, key = '') => {
  const widget = getDashboardWidget(dashboardWidgets, key);
  const datasets = widget?.payload?.datasets || widget?.datasets || {};
  const first = getFirstDataset(datasets);
  return Array.isArray(first?.categories) ? first.categories : [];
};

const buildOpenRoleRiskSection = ({ report, widgetModels = [], dashboardWidgets = {} }) => {
  const dataset = getWidgetDataset(widgetModels, 'open_roles_risk') || getDashboardWidgetDataset(dashboardWidgets, 'open_roles_risk');
  const categories = Array.isArray(dataset?.categories) ? dataset.categories : getDashboardCategories(dashboardWidgets, 'open_roles_risk');
  const rows = categories.length
    ? categories.map((row, index) => ({
      id: row?.id || `role-risk-${index}`,
      label: row?.label || row?.jobTitle || `Role ${index + 1}`,
      value: safeNumber(row?.metrics?.riskScore ?? row?.riskScore),
      values: {
        role: row?.label || row?.jobTitle || `Role ${index + 1}`,
        daysOpen: safeNumber(row?.metrics?.daysOpen ?? row?.daysOpen),
        pipelineDepth: safeNumber(row?.metrics?.pipelineDepth ?? row?.pipelineDepth ?? row?.activeCandidates),
        stalledCandidates: safeNumber(row?.metrics?.stalledCandidates ?? row?.stalledCandidates),
        riskScore: safePct(row?.metrics?.riskScore ?? row?.riskScore)
      }
    }))
    : clampRows(report?.series?.jobHealth, 10)
      .map((row, index) => {
        const riskLoad = safeNumber(row?.stalledCandidates) + safeNumber(row?.untouchedApplicants) + safeNumber(row?.lateStageLosses);
        return {
          id: row?.jobId || `role-risk-${index}`,
          label: row?.jobTitle || `Role ${index + 1}`,
          value: riskLoad,
          values: {
            role: row?.jobTitle || `Role ${index + 1}`,
            daysOpen: safeNumber(row?.daysOpen),
            pipelineDepth: safeNumber(row?.activeCandidates),
            stalledCandidates: safeNumber(row?.stalledCandidates),
            riskScore: riskLoad
          }
        };
      });

  return {
    id: 'open_role_risk',
    title: SECTION_TITLES.open_role_risk,
    description: SECTION_DESCRIPTIONS.open_role_risk,
    type: EXPORT_SECTION_TYPES.RANKED_TABLE,
    columns: [
      { id: 'role', label: 'Role' },
      { id: 'daysOpen', label: 'Days open' },
      { id: 'pipelineDepth', label: 'Pipeline' },
      { id: 'stalledCandidates', label: 'Stalled' },
      { id: 'riskScore', label: 'Risk' }
    ],
    rows: clampRows(rows.sort((left, right) => safeNumber(right.value) - safeNumber(left.value)), 10)
  };
};

const buildHistoricalRoleRiskSection = ({ report, riskHistory = [], dashboardWidgets = {} }) => {
  const dataset = getDashboardWidgetDataset(dashboardWidgets, 'historical_role_risk');
  const dashboardRows = Array.isArray(dataset?.rows) ? dataset.rows : [];
  const reportRows = Array.isArray(report?.riskHistory) ? report.riskHistory : [];
  const sourceRows = (Array.isArray(riskHistory) && riskHistory.length ? riskHistory : reportRows.length ? reportRows : dashboardRows);
  const rows = clampRows(sourceRows, 10).map((row, index) => {
    const role = row?.role || row?.jobTitle || row?.label || row?.jobId || `Role ${index + 1}`;
    const status = row?.status || row?.lifecycleStatus || 'published';
    const peakRisk = safeNumber(row?.peakRisk ?? row?.riskScore);
    const highRiskWeeks = safeNumber(row?.highRiskWeeks ?? (peakRisk >= 70 ? 1 : 0));
    const maxDaysOpen = safeNumber(row?.maxDaysOpen ?? row?.daysOpen);
    return {
      id: row?.jobId || row?.id || `historical-role-risk-${index}`,
      label: role,
      value: peakRisk + (highRiskWeeks * 10),
      values: {
        role,
        status,
        peakRisk: peakRisk ? safePct(peakRisk) : '0%',
        highRiskWeeks,
        maxDaysOpen: `${maxDaysOpen}d`,
        lastSeen: row?.lastSeen || row?.week || row?.computedAt || ''
      }
    };
  });

  return {
    id: 'historical_role_risk',
    title: SECTION_TITLES.historical_role_risk,
    description: SECTION_DESCRIPTIONS.historical_role_risk,
    type: EXPORT_SECTION_TYPES.RANKED_TABLE,
    columns: [
      { id: 'role', label: 'Role' },
      { id: 'status', label: 'Status' },
      { id: 'peakRisk', label: 'Peak risk' },
      { id: 'highRiskWeeks', label: 'High-risk weeks' },
      { id: 'maxDaysOpen', label: 'Max days open' },
      { id: 'lastSeen', label: 'Last seen' }
    ],
    rows: rows.length
      ? rows.sort((left, right) => safeNumber(right.value) - safeNumber(left.value))
      : [{
        id: 'historical-role-risk-empty',
        label: 'No historical role-risk snapshots yet',
        value: 0,
        values: {
          role: 'No historical role-risk snapshots yet',
          status: 'Snapshot history starts after the nightly analytics job runs',
          peakRisk: '--',
          highRiskWeeks: '--',
          maxDaysOpen: '--',
          lastSeen: '--'
        }
      }]
  };
};

const buildAgingRolesSection = ({ report, widgetModels = [], dashboardWidgets = {} }) => {
  const dataset = getWidgetDataset(widgetModels, 'open_roles_risk') || getDashboardWidgetDataset(dashboardWidgets, 'open_roles_risk');
  const categories = Array.isArray(dataset?.categories) ? dataset.categories : [];
  if (categories.length) {
    return {
      id: 'aging_roles',
      title: SECTION_TITLES.aging_roles,
      description: SECTION_DESCRIPTIONS.aging_roles,
      type: EXPORT_SECTION_TYPES.RANKED_TABLE,
      columns: [
        { id: 'role', label: 'Role' },
        { id: 'daysOpen', label: 'Days open' },
        { id: 'pipelineDepth', label: 'Pipeline' },
        { id: 'stalledCandidates', label: 'Stalled' },
        { id: 'riskScore', label: 'Risk' }
      ],
      rows: clampRows(categories
        .map((category, index) => {
          const metrics = category?.metrics || {};
          return {
            id: category?.id || `aging-role-${index}`,
            label: category?.label || `Role ${index + 1}`,
            value: safeNumber(metrics.daysOpen ?? category?.daysOpen),
            values: {
              role: category?.label || `Role ${index + 1}`,
              daysOpen: `${safeNumber(metrics.daysOpen ?? category?.daysOpen)}d`,
              pipelineDepth: safeNumber(metrics.pipelineDepth ?? category?.pipelineDepth ?? category?.activeCandidates),
              stalledCandidates: safeNumber(metrics.stalledCandidates ?? category?.stalledCandidates),
              riskScore: safePct(metrics.riskScore ?? category?.riskScore)
            }
          };
        })
        .filter((row) => safeNumber(row.value) > 0)
        .sort((left, right) => safeNumber(right.value) - safeNumber(left.value)), 10)
    };
  }

  return {
    id: 'aging_roles',
    title: SECTION_TITLES.aging_roles,
    description: SECTION_DESCRIPTIONS.aging_roles,
    type: EXPORT_SECTION_TYPES.RANKED_TABLE,
    columns: [
      { id: 'role', label: 'Role' },
      { id: 'daysOpen', label: 'Days open' },
      { id: 'applications', label: 'Applicants' },
      { id: 'activeCandidates', label: 'Active candidates' },
      { id: 'hires', label: 'Hires' }
    ],
    rows: clampRows([...(report?.operationalSignals?.agingOpenJobs || []), ...(report?.series?.jobHealth || [])]
      .filter((row) => safeNumber(row?.daysOpen) > 0)
      .sort((left, right) => safeNumber(right?.daysOpen) - safeNumber(left?.daysOpen)), 10)
      .map((row, index) => ({
        id: row?.jobId || `aging-role-${index}`,
        label: row?.jobTitle || `Role ${index + 1}`,
        value: safeNumber(row?.daysOpen),
        values: {
          role: row?.jobTitle || `Role ${index + 1}`,
          daysOpen: `${safeNumber(row?.daysOpen)}d`,
          applications: safeNumber(row?.applications),
          activeCandidates: safeNumber(row?.activeCandidates),
          hires: safeNumber(row?.hires)
        }
      }))
  };
};

const buildPipelineBacklogSection = (report) => ({
  id: 'pipeline_backlog',
  title: SECTION_TITLES.pipeline_backlog,
  description: SECTION_DESCRIPTIONS.pipeline_backlog,
  type: EXPORT_SECTION_TYPES.BAR_TABLE,
  columns: [
    { id: 'segment', label: 'Segment' },
    { id: 'count', label: 'Active candidates' },
    { id: 'share', label: 'Share' }
  ],
  rows: clampRows(report?.series?.pipelineLoad, 10).map((row, index) => ({
    id: row?.id || `pipeline-${index}`,
    label: row?.label || row?.id || `Segment ${index + 1}`,
    value: safeNumber(row?.count),
    values: {
      segment: row?.label || row?.id || `Segment ${index + 1}`,
      count: safeNumber(row?.count),
      share: safePct(row?.activePipelineSharePct)
    }
  }))
});

const buildOfferHealthSection = (report) => {
  const funnel = report?.funnel || {};
  const metrics = report?.metrics || {};
  return {
    id: 'offer_health',
    title: SECTION_TITLES.offer_health,
    description: SECTION_DESCRIPTIONS.offer_health,
    type: EXPORT_SECTION_TYPES.RANKED_TABLE,
    columns: [
      { id: 'stage', label: 'Offer stage' },
      { id: 'count', label: 'Count' },
      { id: 'context', label: 'Context' }
    ],
    rows: [
      ['offers_submitted', 'Offers submitted', `${safePct(metrics.interviewToOfferRatePct)} interview-to-offer`],
      ['offers_approved', 'Offers approved', `${safePct(metrics.offerApprovalRatePct)} approval rate`],
      ['offers_sent', 'Offers sent', uiText("{{count}} hires", { count: safeNumber(funnel.hires) })],
      ['hires', 'Hires', `${safePct(metrics.hireRatePct)} hire rate`]
    ].map(([key, label, context]) => ({
      id: key,
      label,
      value: safeNumber(funnel[key]),
      values: {
        stage: label,
        count: safeNumber(funnel[key]),
        context
      }
    }))
  };
};

const buildActionsSection = ({ report, alerts = [], targets = [] }) => {
  const alertItems = (alerts.length ? alerts : report?.alerts || []).map((alert, index) => ({
    id: alert?.id || `alert-${index}`,
    label: alert?.title || alert?.message || `Alert ${index + 1}`,
    detail: alert?.message || alert?.metricKey || ''
  }));
  const insightItems = (report?.insights || []).map((insight, index) => ({
    id: insight?.id || `insight-${index}`,
    label: insight?.title || insight?.summary || `Insight ${index + 1}`,
    detail: insight?.why || insight?.description || ''
  }));
  const targetItems = (targets || []).map((target, index) => ({
    id: target?.id || `target-${index}`,
    label: target?.title || target?.metricKey || `Target ${index + 1}`,
    detail: target?.message || target?.scope || ''
  }));

  return {
    id: 'recommended_actions',
    title: SECTION_TITLES.recommended_actions,
    description: SECTION_DESCRIPTIONS.recommended_actions,
    type: EXPORT_SECTION_TYPES.ACTION_LIST,
    items: clampRows([...alertItems, ...insightItems, ...targetItems], 8)
  };
};

const buildConfidenceSection = (report) => {
  const quality = report?.dataQuality || {};
  const excluded = report?.excludedLegacyCounts || {};
  const warnings = [...(report?.reportWarnings || []), ...(quality.warnings || [])].filter(Boolean);
  return {
    id: 'data_confidence',
    title: SECTION_TITLES.data_confidence,
    description: SECTION_DESCRIPTIONS.data_confidence,
    type: EXPORT_SECTION_TYPES.CONFIDENCE_NOTES,
    metrics: [
      { id: 'coverage', label: 'Data coverage', value: quality.coverageScore === null || quality.coverageScore === undefined ? '--' : `${quality.coverageScore}%` },
      { id: 'observed', label: 'Verified event share', value: `${safeNumber(quality.observedEventPct)}%` },
      { id: 'historical', label: 'Historical snapshot fill', value: `${safeNumber(quality.inferredEventPct)}%` },
      { id: 'legacy', label: 'Legacy views excluded', value: safeNumber(excluded.legacyLifetimeJobViews) },
      { id: 'missing', label: 'Subjects missing application event', value: safeNumber(excluded.subjectsMissingApplicationEvent) }
    ],
    items: warnings.length ? warnings : ['No data quality warnings for this report.']
  };
};

const buildGenericWidgetSection = (sectionId, widgetModels = [], widgetIds = [], dashboardWidgets = {}) => {
  const model = widgetIds.map((id) => (widgetModels || []).find((item) => item?.id === id)).find(Boolean);
  const dataset = widgetIds
    .map((id) => getWidgetDataset(widgetModels, id) || getDashboardWidgetDataset(dashboardWidgets, id))
    .find(datasetHasContent) || null;
  const dashboardWidget = widgetIds.map((id) => getDashboardWidget(dashboardWidgets, id)).find(Boolean);
  const sectionBase = {
    id: sectionId,
    title: SECTION_TITLES[sectionId] || model?.title || dashboardWidget?.title || sectionId,
    description: SECTION_DESCRIPTIONS[sectionId] || model?.title || dashboardWidget?.title || '',
    type: EXPORT_SECTION_TYPES.RANKED_TABLE,
    columns: [
      { id: 'item', label: 'Item' },
      { id: 'value', label: 'Value' },
      { id: 'context', label: 'Context' }
    ],
    rows: []
  };

  if (!dataset) return sectionBase;

  if (Array.isArray(dataset.rows) && Array.isArray(dataset.columns)) {
    return {
      ...sectionBase,
      columns: dataset.columns.slice(0, 5).map((column) => ({ id: column.id, label: column.label || column.id })),
      rows: clampRows(dataset.rows, 10).map((row, index) => ({
        id: row.id || `${sectionId}-${index}`,
        label: row.label || `Row ${index + 1}`,
        value: safeNumber(Object.values(row.values || {})[0]),
        values: dataset.columns.slice(0, 5).reduce((values, column) => ({
          ...values,
          [column.id]: row.values?.[column.id] ?? ''
        }), {})
      }))
    };
  }

  if (Array.isArray(dataset.categories)) {
    const metrics = dataset.metrics || [];
    const selectedMetrics = metrics.slice(0, 4);
    return {
      ...sectionBase,
      columns: [
        { id: 'item', label: 'Item' },
        ...selectedMetrics.map((metric) => ({ id: metric.id, label: metric.label || metric.id }))
      ],
      rows: clampRows(dataset.categories, 10).map((category, index) => ({
        id: category.id || `${sectionId}-${index}`,
        label: category.label || `Item ${index + 1}`,
        value: safeNumber(category.metrics?.[selectedMetrics[0]?.id]),
        values: {
          item: category.label || `Item ${index + 1}`,
          ...selectedMetrics.reduce((values, metric) => ({
            ...values,
            [metric.id]: category.metrics?.[metric.id] ?? ''
          }), {})
        }
      }))
    };
  }

  if (Array.isArray(dataset.items)) {
    return {
      ...sectionBase,
      type: EXPORT_SECTION_TYPES.TARGET_PROGRESS,
      columns: [
        { id: 'item', label: 'Target' },
        { id: 'actual', label: 'Actual' },
        { id: 'target', label: 'Target' },
        { id: 'progress', label: 'Progress' }
      ],
      rows: clampRows(dataset.items, 10).map((item, index) => ({
        id: item.id || `${sectionId}-${index}`,
        label: item.label || `Target ${index + 1}`,
        value: safeNumber(item.progressPct ?? item.percentComplete ?? item.progress),
        values: {
          item: item.label || `Target ${index + 1}`,
          actual: item.actual ?? item.value ?? '',
          target: item.target ?? '',
          progress: item.progressPct !== undefined
            ? safePct(item.progressPct)
            : item.percentComplete !== undefined
              ? safePct(item.percentComplete)
              : item.progress ?? ''
        }
      }))
    };
  }

  if (Array.isArray(dataset.periods) && Array.isArray(dataset.series)) {
    const selectedSeries = dataset.series.slice(0, 4);
    return {
      ...sectionBase,
      type: EXPORT_SECTION_TYPES.TREND,
      columns: [
        { id: 'period', label: 'Period' },
        ...selectedSeries.map((series) => ({ id: series.id, label: series.label || series.id }))
      ],
      rows: clampRows(dataset.periods, 10).map((period, index) => ({
        id: period.id || period.label || `${sectionId}-${index}`,
        label: period.label || period.id || `Period ${index + 1}`,
        value: safeNumber(selectedSeries[0]?.values?.[index]),
        values: {
          period: period.label || period.id || `Period ${index + 1}`,
          ...selectedSeries.reduce((values, series) => ({
            ...values,
            [series.id]: series.values?.[index] ?? ''
          }), {})
        }
      }))
    };
  }

  if (Array.isArray(dataset.countries)) {
    return {
      ...sectionBase,
      type: EXPORT_SECTION_TYPES.RANKED_TABLE,
      columns: [
        { id: 'location', label: 'Location' },
        { id: 'count', label: 'Count' },
        { id: 'share', label: 'Share' }
      ],
      rows: clampRows(dataset.countries, 10).map((country, index) => ({
        id: country.id || country.code || `${sectionId}-${index}`,
        label: country.label || country.name || country.code || `Location ${index + 1}`,
        value: safeNumber(country.count ?? country.value),
        values: {
          location: country.label || country.name || country.code || `Location ${index + 1}`,
          count: safeNumber(country.count ?? country.value),
          share: country.sharePct !== undefined ? safePct(country.sharePct) : ''
        }
      }))
    };
  }

  if (Array.isArray(dataset.profiles)) {
    const selectedMetrics = (dataset.metrics || []).slice(0, 4);
    return {
      ...sectionBase,
      columns: [
        { id: 'profile', label: 'Profile' },
        ...selectedMetrics.map((metric) => ({ id: metric.id, label: metric.label || metric.id }))
      ],
      rows: clampRows(dataset.profiles, 10).map((profile, index) => ({
        id: profile.id || `${sectionId}-${index}`,
        label: profile.label || profile.name || `Profile ${index + 1}`,
        value: safeNumber(profile.metrics?.[selectedMetrics[0]?.id]),
        values: {
          profile: profile.label || profile.name || `Profile ${index + 1}`,
          ...selectedMetrics.reduce((values, metric) => ({
            ...values,
            [metric.id]: profile.metrics?.[metric.id] ?? ''
          }), {})
        }
      }))
    };
  }

  if (Array.isArray(dataset.lanes)) {
    return {
      ...sectionBase,
      columns: [
        { id: 'item', label: 'Item' },
        { id: 'openItems', label: 'Open items' },
        { id: 'pastThreshold', label: 'Past threshold' },
        { id: 'oldestWait', label: 'Oldest wait' },
        { id: 'threshold', label: 'Threshold' }
      ],
      rows: clampRows(dataset.lanes, 10).map((lane, index) => ({
        id: lane.id || `${sectionId}-${index}`,
        label: lane.label || lane.title || `Item ${index + 1}`,
        value: safeNumber(lane.breachedCount ?? lane.segments?.length ?? lane.progressPct ?? lane.riskScore),
        values: {
          item: lane.label || lane.title || `Item ${index + 1}`,
          openItems: Array.isArray(lane.segments) ? lane.segments.length : safeNumber(lane.openItems ?? lane.count),
          pastThreshold: safeNumber(lane.breachedCount ?? lane.pastThreshold),
          oldestWait: lane.oldestDays !== undefined ? `${safeNumber(lane.oldestDays)}d` : '',
          threshold: lane.thresholdDays !== undefined ? `${safeNumber(lane.thresholdDays)}d` : ''
        }
      }))
    };
  }

  if (Array.isArray(dataset.points)) {
    return {
      ...sectionBase,
      columns: [
        { id: 'item', label: 'Item' },
        { id: 'x', label: dataset.xLabel || 'X' },
        { id: 'y', label: dataset.yLabel || 'Y' },
        { id: 'size', label: dataset.sizeLabel || 'Size' }
      ],
      rows: clampRows(dataset.points, 10).map((point, index) => ({
        id: point.id || `${sectionId}-${index}`,
        label: point.label || point.name || `Point ${index + 1}`,
        value: safeNumber(point.y ?? point.value),
        values: {
          item: point.label || point.name || `Point ${index + 1}`,
          x: point.x ?? '',
          y: point.y ?? point.value ?? '',
          size: point.size ?? point.count ?? ''
        }
      }))
    };
  }

  if (Array.isArray(dataset.stages)) {
    return {
      ...sectionBase,
      type: EXPORT_SECTION_TYPES.BAR_TABLE,
      columns: [
        { id: 'stage', label: 'Stage' },
        { id: 'count', label: 'Count' },
        { id: 'conversionRate', label: 'Conversion' },
        { id: 'dropoffCount', label: 'Drop-off' }
      ],
      rows: clampRows(dataset.stages, 10).map((stage, index) => ({
        id: stage.id || `${sectionId}-${index}`,
        label: stage.label || `Stage ${index + 1}`,
        value: safeNumber(stage.count),
        values: {
          stage: stage.label || `Stage ${index + 1}`,
          count: safeNumber(stage.count),
          conversionRate: safePct(stage.conversionRate),
          dropoffCount: safeNumber(stage.dropoffCount)
        }
      }))
    };
  }

  return sectionBase;
};

const hasSectionContent = (section = {}) =>
  Boolean((section.rows || []).length || (section.metrics || []).length || (section.items || []).length);

const buildSection = ({ screenId = '', sectionId, report, widgetModels, dashboardWidgets, alerts, targets, extraContext, riskHistory = EMPTY_ARRAY }) => {
  switch (sectionId) {
    case 'executive_summary':
      return buildKpiSection(report);
    case 'funnel_conversion':
      return buildFunnelSection(report);
    case 'pipeline_conversion':
      {
        const section = buildGenericWidgetSection(sectionId, widgetModels, ['pipeline_conversion'], dashboardWidgets);
        return hasSectionContent(section) ? section : buildFunnelSection(report);
      }
    case 'open_role_risk':
      return buildOpenRoleRiskSection({ report, widgetModels, dashboardWidgets });
    case 'historical_role_risk':
      return buildHistoricalRoleRiskSection({ report, riskHistory, dashboardWidgets });
    case 'source_performance':
      return buildSourceSection(report);
    case 'source_quality':
      {
        const section = buildGenericWidgetSection(sectionId, widgetModels, ['source_quality', 'source_quality_matrix'], dashboardWidgets);
        return hasSectionContent(section) ? section : buildSourceSection(report, sectionId);
      }
    case 'quality_signals':
      return buildGenericWidgetSection(sectionId, widgetModels, ['early_quality_index', 'onboarding_health', 'probation_performance'], dashboardWidgets);
    case 'team_execution':
      return buildGenericWidgetSection(sectionId, widgetModels, ['recruiter_scorecard', 'load_vs_outcome', 'recruiter_radar'], dashboardWidgets);
    case 'recommended_actions':
      return buildActionsSection({ report, alerts, targets });
    case 'data_confidence':
      return buildConfidenceSection(report);
    case 'aging_roles':
      return buildAgingRolesSection({ report, widgetModels, dashboardWidgets });
    case 'pipeline_backlog':
      return buildPipelineBacklogSection(report);
    case 'offer_health':
      {
        const section = buildGenericWidgetSection(sectionId, widgetModels, getOfferHealthWidgetIds(screenId), dashboardWidgets);
        return hasSectionContent(section) ? section : buildOfferHealthSection(report);
      }
    case 'headcount_targets':
      {
        const section = buildGenericWidgetSection(sectionId, widgetModels, ['headcount_targets_by_tag_group', 'headcount_targets'], dashboardWidgets);
        return hasSectionContent(section) ? section : buildHeadcountExtraSection(extraContext);
      }
    case 'stage_bottlenecks':
      return buildGenericWidgetSection(sectionId, widgetModels, ['stage_bottleneck'], dashboardWidgets);
    case 'vacancy_forecast':
      return buildGenericWidgetSection(sectionId, widgetModels, ['vacancy_forecast'], dashboardWidgets);
    case 'onboarding_health':
      return buildGenericWidgetSection(sectionId, widgetModels, ['onboarding_health'], dashboardWidgets);
    case 'retention_signals':
      return buildGenericWidgetSection(sectionId, widgetModels, ['retention_timeline', 'retention_by_recruiter'], dashboardWidgets);
    case 'probation_signals':
      return buildGenericWidgetSection(sectionId, widgetModels, ['probation_performance'], dashboardWidgets);
    case 'recruiter_workload':
      return buildGenericWidgetSection(sectionId, widgetModels, ['load_vs_outcome', 'recruiter_scorecard'], dashboardWidgets);
    case 'recruiter_velocity':
      return buildGenericWidgetSection(sectionId, widgetModels, ['recruiter_velocity', 'time_to_fill'], dashboardWidgets);
    default:
      return buildGenericWidgetSection(sectionId, widgetModels, [], dashboardWidgets);
  }
};

const buildHeadcountExtraSection = (extraContext = {}) => ({
  id: 'headcount_targets',
  title: SECTION_TITLES.headcount_targets,
  description: SECTION_DESCRIPTIONS.headcount_targets,
  type: EXPORT_SECTION_TYPES.TARGET_PROGRESS,
  columns: [
    { id: 'department', label: 'Department' },
    { id: 'filled', label: 'Filled' },
    { id: 'target', label: 'Target' },
    { id: 'progress', label: 'Progress' }
  ],
  rows: clampRows(extraContext?.headcountPlan?.departments, 10).map((row, index) => ({
    id: row?.id || `target-${index}`,
    label: row?.label || `Department ${index + 1}`,
    value: safeNumber(row?.progressPct ?? row?.progress),
    values: {
      department: row?.label || `Department ${index + 1}`,
      filled: safeNumber(row?.filled),
      target: safeNumber(row?.target),
      progress: row?.progressPct !== undefined ? safePct(row.progressPct) : row?.progress || ''
    }
  }))
});

const normalizeDocument = ({ id, title, subtitle, report, filterState, sections, companyName = 'JOBSEEQR Employer' }) => ({
  id,
  title,
  subtitle,
  companyName,
  generatedAt: report?.generatedAt || new Date().toISOString(),
  dateRangeDays: report?.view?.dateRangeDays || getAnalyticsPeriodDays(filterState?.period || filterState?.dateRangeDays || 30),
  sections: (sections || []).filter(Boolean),
  dataQuality: report?.dataQuality || {}
});

export const buildCanonicalAnalyticsExportDocument = ({
  screenId,
  report,
  widgetModels = EMPTY_ARRAY,
  dashboardWidgets = {},
  riskHistory = EMPTY_ARRAY,
  filterState = {},
  alerts = EMPTY_ARRAY,
  targets = EMPTY_ARRAY,
  extraContext = {},
  companyName = 'JOBSEEQR Employer'
}) => {
  const manifest = CANONICAL_SCREEN_MANIFESTS[screenId] || CANONICAL_SCREEN_MANIFESTS[ANALYTICS_SCREEN_IDS.EXECUTIVE_OVERVIEW];
  return normalizeDocument({
    id: screenId,
    title: manifest.title,
    subtitle: manifest.subtitle,
    report,
    filterState,
    companyName,
    sections: manifest.sectionIds.map((sectionId) =>
      buildSection({ screenId, sectionId, report, widgetModels, dashboardWidgets, alerts, targets, extraContext, riskHistory })
    )
  });
};

export const buildReportCenterExportDocument = ({
  templateId = REPORT_CENTER_TEMPLATES[0].id,
  enabledSectionIds = null,
  report,
  widgetModels = EMPTY_ARRAY,
  dashboardWidgets = {},
  riskHistory = EMPTY_ARRAY,
  filterState = {},
  alerts = EMPTY_ARRAY,
  targets = EMPTY_ARRAY,
  companyName = 'JOBSEEQR Employer'
}) => {
  const template = getReportCenterTemplate(templateId);
  const enabled = new Set(Array.isArray(enabledSectionIds) ? enabledSectionIds : template.sectionIds);
  return normalizeDocument({
    id: template.id,
    title: template.label,
    subtitle: template.subtitle,
    report,
    filterState,
    companyName,
    sections: template.sectionIds
      .filter((sectionId) => enabled.has(sectionId))
      .map((sectionId) => buildSection({ sectionId, report, widgetModels, dashboardWidgets, alerts, targets, riskHistory }))
  });
};
